import { Box } from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloseIcon from '@mui/icons-material/Close'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { differenceInCalendarMonths } from 'date-fns'
import React, { ChangeEventHandler, useEffect, useRef, useState } from 'react'
import { DateField, useNotify } from 'react-admin'
import { Divider, Grid, Relative, TextClamp, Wrap } from 'shared/components/Base/Base'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Button } from 'shared/components/Button/Button'
import { Checkbox } from 'shared/components/Checkbox/Checkbox'
import { SimpleDialog } from 'shared/components/Dialog/SimpleDialog'
import { Dropdown } from 'shared/components/Dropdown/Dropdown'
import { Arrow } from 'shared/components/Dropdown/Dropdown.styles'
import { ExternalLink } from 'shared/components/ExternalLink/ExternalLink'
import { Notes } from 'shared/components/Notes/Notes'
import { InlineSpinner } from 'shared/components/Spinner'
import { Text } from 'shared/components/Text/Text'
import { formatMonths } from 'shared/helpers/formatters'
import { Handler, HandlerOf } from 'shared/helpers/typeHelper'
import { UnemploymentGapBeforeReasonDetail } from 'shared/models/employment'
import { Activity, Note } from 'shared/models/note'
import { generateEmploymentJobsDocument, generateReleaseForm, User } from 'shared/models/user'
import {
  bgGray,
  cyanD1,
  darkGray,
  error,
  errorD1,
  kellyGreenD1,
  lightGray1,
  marigoldOrange,
  midGray,
  primaryBlack,
  royalBlue,
  success,
  successD1,
  warning,
} from 'shared/theme/colors'
import styled from 'styled-components'

import { DocumentAttachmentType } from 'helpers/documents'
import { createNote } from '../../api/notes'
import { captureExceptionIfProduction } from '../../helpers/utils'
import {
  changeEmploymentJobStatus,
  EmploymentHistory,
  EmploymentHistoryJobType,
  EmploymentHistoryStatus,
  EmploymentHistoryStatusLabels,
  uploadEmploymentDocument,
  uploadEmploymentJobDocument,
  useEmploymentHistory,
  useEmploymentHistoryMonthsEmployed,
} from '../../models/employment'
import { hasPermission, Permissions } from '../../Permissions'
import { UserEmploymentForm } from './UserEmploymentForm'
import { UserEmploymentVerifyDates } from './UserEmploymentVerifyDates'

const NOTES_MAX_HEIGHT = 180
const ALL_JOBS_YEARS = 100

export function UserEmployment({
  user,
  refetchUser,
  permissions,
}: {
  user: User
  refetchUser: () => Promise<any>
  permissions: any
}) {
  const canSeeEmployment = permissions && hasPermission(permissions, Permissions.ViewEmploymentJob)
  const [isAdding, setIsAdding] = useState(false)
  const [isEditing, setIsEditing] = useState<EmploymentHistory | null>(null)
  const [showAllJobs, setShowAllJobs] = useState(false)
  const [isGeneratingReleaseForm, setIsGeneratingReleaseForm] = useState(false)
  const [isGeneratingJobsDocument, setIsGeneratingJobsDocument] = useState(false)
  const [isPspFileUploadLoading, setIsPspFileUploadLoading] = useState(false)

  const notify = useNotify()
  const {
    employmentHistory,
    refetch: refetchEmploymentHistory,
    isLoading: isLoadingEmploymentHistory,
  } = useEmploymentHistory({ userId: user.id, monthsBack: showAllJobs ? undefined : 36 })

  const {
    monthsEmployed: monthsEmployedInLastYear,
    maxEndDate,
    refetch: refetchMonthsEmployedInLastYear,
  } = useEmploymentHistoryMonthsEmployed({
    userId: user.id,
    years: 1,
  })

  const { monthsEmployed: monthsEmployedInLast3Years, refetch: refetchMonthsEmployedInLast3Years } =
    useEmploymentHistoryMonthsEmployed({
      userId: user.id,
      years: 3,
    })

  const { monthsEmployed: totalVerifiedMonthsEmployed, refetch: refetchTotalVerifiedMonthsEmployed } =
    useEmploymentHistoryMonthsEmployed({
      userId: user.id,
      statuses: [EmploymentHistoryStatus.VERIFIED, EmploymentHistoryStatus.VERIFIED_WITH_DISCREPANCY],
      years: ALL_JOBS_YEARS,
    })

  const canEdit = permissions && hasPermission(permissions, Permissions.ChangeEmploymentJob)

  function handleEdit(job: EmploymentHistory) {
    setIsEditing(job)
  }

  async function handleGenerateReleaseForm() {
    setIsGeneratingReleaseForm(true)
    try {
      await generateReleaseForm(user.id)
      await refetchUser()
    } catch (error: any) {
      notify(`Unable to generate release form: ${error.response.data}`, { type: 'error' })
      captureExceptionIfProduction(error)
    }
    setIsGeneratingReleaseForm(false)
  }

  async function handleGenerateEmploymentJobsDocument() {
    setIsGeneratingJobsDocument(true)
    try {
      await generateEmploymentJobsDocument(user.id)
      await refetchUser()
    } catch (error: any) {
      notify(`Unable to generate release form: ${error.response.data}`, { type: 'error' })
      captureExceptionIfProduction(error)
    }
    setIsGeneratingJobsDocument(false)
  }

  async function handleEditSuccess() {
    await Promise.all([
      refetchEmploymentHistory(),
      refetchMonthsEmployedInLastYear(),
      refetchMonthsEmployedInLast3Years(),
      refetchTotalVerifiedMonthsEmployed(),
    ])
    notify('Position updated', { type: 'success' })
    window.scrollTo({ top: 0 })
    setIsEditing(null)
    setIsAdding(false)
  }

  async function handlePspFileUpload(file: File) {
    setIsPspFileUploadLoading(true)
    const formData = new FormData()
    formData.append('document', file)

    try {
      await uploadEmploymentDocument(user.id, formData, DocumentAttachmentType.PSP_REPORT_DOCUMENT)
      await refetchUser()
    } catch (error: any) {
      notify(`Unable to upload file: ${error.response.data}`, { type: 'error' })
      captureExceptionIfProduction(error)
    }
    setIsPspFileUploadLoading(false)
  }

  function handleEditClose() {
    window.scrollTo({ top: 0 })
    setIsEditing(null)
    setIsAdding(false)
  }

  if (!canSeeEmployment)
    return <Text variant="paragraph1">You don't have permissions to view Employment</Text>

  if (!user || !employmentHistory) {
    return <InlineSpinner mt={5} mb={5} />
  }

  if (isEditing) {
    return (
      <UserEmploymentForm
        employmentJob={isEditing}
        onSuccess={handleEditSuccess}
        onClose={handleEditClose}
        userId={user.id}
      />
    )
  }

  if (isAdding) {
    return <UserEmploymentForm onSuccess={handleEditSuccess} onClose={handleEditClose} userId={user.id} />
  }

  return (
    <>
      <Box mt={3} />
      <FlexBox gap="40px">
        <SummaryBox>
          <FlexBox justifyContent="space-between">
            <div>
              <Text variant="label4" fontColor={lightGray1}>
                Submitted on
              </Text>
              <Text variant="paragraph1" fontColor={darkGray} mt={0.5} fontWeight={400}>
                <DateField
                  record={user.defaultTeamMembership}
                  showTime={true}
                  source="driverDateTermsAccepted"
                  options={{ timeZoneName: 'short' }}
                />
              </Text>
            </div>

            <div>
              <Text variant="label4" fontColor={lightGray1}>
                Total verified
              </Text>
              <Text variant="header2" fontColor={darkGray} mt={0.5} fontWeight={400}>
                {totalVerifiedMonthsEmployed || 0} month{totalVerifiedMonthsEmployed > 1 ? 's' : ''}
              </Text>
            </div>
          </FlexBox>

          <div>
            <Text variant="label4" fontColor={lightGray1}>
              Verified months max date
            </Text>
            <Text variant="paragraph1" fontColor={darkGray} mt={0.5} fontWeight={400}>
              {maxEndDate}
            </Text>
          </div>

          <Text variant="label4" fontColor={lightGray1} mt={1}>
            Class 8 jobs in last 1 year / 3 years (at the time of submission)
          </Text>
          <Text variant="paragraph1" fontColor={darkGray} mt={0.5} fontWeight={400}>
            {monthsEmployedInLastYear || 0} month{monthsEmployedInLastYear > 1 ? 's' : ''} /{' '}
            {monthsEmployedInLast3Years || 0} month{monthsEmployedInLast3Years > 1 ? 's' : ''}
          </Text>
        </SummaryBox>

        <SummaryBox>
          <Text variant="uppercaseLabel2">
            Documents (
            {
              [
                user.employmentJobsDocument,
                user.employmentReleaseDocument,
                user.employmentTenstreetDocument,
              ].filter(Boolean).length
            }
            )
          </Text>

          <Text variant="header3" fontColor={lightGray1} mt={2}>
            <Grid gap="20px" columns="200px 1fr">
              <SummaryBoxLabel>CT application </SummaryBoxLabel>

              <FlexBox gap="20px">
                {user.employmentJobsDocument ? (
                  <ExternalLink href={user.employmentJobsDocument}>View</ExternalLink>
                ) : null}

                <Button
                  onClick={handleGenerateEmploymentJobsDocument}
                  variant="text"
                  margin="0 auto 0 0"
                  isLoading={isGeneratingJobsDocument}
                >
                  <Text variant="header4" fontColor={royalBlue}>
                    Generate new
                  </Text>
                </Button>
              </FlexBox>
            </Grid>
          </Text>
          <Text variant="header3" fontColor={lightGray1} mt={1}>
            <Grid gap="20px" columns="200px 1fr">
              <SummaryBoxLabel>Employer release form</SummaryBoxLabel>

              <FlexBox gap="20px">
                {user.employmentReleaseDocument ? (
                  <ExternalLink href={user.employmentReleaseDocument}>View</ExternalLink>
                ) : null}

                <Button
                  onClick={handleGenerateReleaseForm}
                  variant="text"
                  margin="0 auto 0 0"
                  isLoading={isGeneratingReleaseForm}
                >
                  <Text variant="header4" fontColor={royalBlue}>
                    Generate new
                  </Text>
                </Button>
              </FlexBox>
            </Grid>
          </Text>
          <Text variant="header3" fontColor={lightGray1} mt={1}>
            <Grid gap="20px" columns="200px 1fr">
              <SummaryBoxLabel>Tenstreet upload</SummaryBoxLabel>
              {user.employmentTenstreetDocument ? (
                <ExternalLink href={user.employmentTenstreetDocument}>View</ExternalLink>
              ) : null}
            </Grid>
          </Text>
          <Text variant="header3" fontColor={lightGray1} mt={1}>
            <Grid gap="20px" columns="200px 5fr 1fr" alignItems="flex-start">
              <SummaryBoxLabel>DAC</SummaryBoxLabel>
              <FlexBox isColumn isFullWidth gap="4px">
                {user.dacReportDocuments.map((document) => (
                  <React.Fragment key={document.url}>
                    <ExternalLink variant="label3" href={document.url}>
                      {document.filename}
                    </ExternalLink>
                  </React.Fragment>
                ))}
              </FlexBox>
            </Grid>
          </Text>
          <Text variant="header3" fontColor={lightGray1} mt={1}>
            <Grid gap="20px" columns="200px 5fr 1fr" alignItems="flex-start">
              <SummaryBoxLabel>PSP</SummaryBoxLabel>
              <FlexBox isColumn isFullWidth gap="4px">
                {user.pspReportDocuments.length === 0 ? (
                  user.driver?.isDriverApplicationFormComplete ? (
                    <Text variant="paragraph2" fontColor={errorD1} mt={0.5} fontWeight={400}>
                      Manual order needed
                    </Text>
                  ) : (
                    <Text variant="paragraph2" fontColor={darkGray} mt={0.5} fontWeight={400}>
                      Not ordered yet
                    </Text>
                  )
                ) : (
                  user.pspReportDocuments.map((document) => (
                    <React.Fragment key={document.url}>
                      <ExternalLink variant="label3" href={document.url}>
                        {document.filename}
                      </ExternalLink>
                    </React.Fragment>
                  ))
                )}
              </FlexBox>
              <FlexBox mt={0.3}>
                <Upload onFileUpload={handlePspFileUpload}>
                  <Button
                    variant="text"
                    icon={<UploadFileIcon htmlColor={royalBlue} fontSize="small" style={{ fontSize: 18 }} />}
                    isLoading={isPspFileUploadLoading}
                  />
                </Upload>
              </FlexBox>
            </Grid>
          </Text>
        </SummaryBox>
      </FlexBox>
      <Box mt={5} />
      <JobsWrap>
        <FlexBox justifyContent="space-between" alignItems="center" mb={4}>
          <Button variant="secondary" size="large" onClick={() => setIsAdding(true)}>
            Add position
          </Button>
          <div>
            <Checkbox
              label="Show jobs beyond 3 years"
              value={showAllJobs}
              onChange={() => {
                setShowAllJobs(!showAllJobs)
              }}
            />
          </div>
        </FlexBox>
        {isLoadingEmploymentHistory && <InlineSpinner mt={5} mb={5} />}

        {employmentHistory.map((job, i) => (
          <EmploymentJob
            key={job.id}
            job={job}
            previousJob={employmentHistory[i - 1]}
            user={user}
            refetchJobs={refetchEmploymentHistory}
            refetchTotalVerifiedMonthsEmployed={refetchTotalVerifiedMonthsEmployed}
            isFirst={i === 0}
            canEdit={canEdit && job.can_edit}
            handleEdit={handleEdit}
          />
        ))}
      </JobsWrap>
      <Box mt={5} />
    </>
  )
}

function EmploymentJob({
  job,
  previousJob,
  user,
  isFirst,
  refetchJobs,
  refetchTotalVerifiedMonthsEmployed,
  handleEdit,
  canEdit,
}: {
  job: EmploymentHistory
  previousJob?: EmploymentHistory
  user: User
  isFirst: boolean
  refetchJobs: Handler
  refetchTotalVerifiedMonthsEmployed: Handler
  handleEdit: HandlerOf<EmploymentHistory>
  canEdit: boolean
}) {
  const notesWrap = useRef<HTMLDivElement>(null)
  const [notes, setNotes] = useState<Note[] | Activity[] | undefined>([])
  const [showSeeMore, setShowSeeMore] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [isEditingVerificationDates, setIsEditingVerificationDates] = useState<EmploymentHistory | null>(null)
  const notify = useNotify()
  const [isFileUploadLoading, setIsFileUploadLoading] = useState(false)
  const isDriving = job.job_type === EmploymentHistoryJobType.DRIVING
  const startDateObject = new Date(`${job.details.start_date}T06:00:00-0800`)
  const endDateObject = job.details.end_date ? new Date(`${job.details.end_date}T06:00:00-0800`) : null

  const startDate = startDateObject.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  })
  const endDate = endDateObject
    ? endDateObject.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    : 'Present'

  function calculateGapBetweenJobs() {
    if (!previousJob || !endDateObject || !previousJob.details?.start_date) return null
    const previousJobStartDate = new Date(`${previousJob.details.start_date}T06:00:00-0800`)

    const monthsGap = differenceInCalendarMonths(previousJobStartDate, endDateObject)
    if (monthsGap < 0) return 0
    if (monthsGap === 0) return 0
    return monthsGap - 1 // Ending job in January, and starting new job in February should be 0 gap
  }
  const gapBetweenJobs = calculateGapBetweenJobs()

  async function handleFileUpload(file: File) {
    setIsFileUploadLoading(true)
    const formData = new FormData()
    formData.append('document', file)

    try {
      await uploadEmploymentJobDocument(job.id, user.id, formData)
      await refetchJobs()
    } catch (error: any) {
      notify(`Unable to upload file: ${error.response.data}`, { type: 'error' })
      captureExceptionIfProduction(error)
    }
    setIsFileUploadLoading(false)
  }

  async function handleJobStatusChange(newStatus: EmploymentHistoryStatus) {
    try {
      await changeEmploymentJobStatus(job.id, user.id, newStatus)
      await createNote(
        'employment-history',
        job.id,
        `${EmploymentHistoryStatusLabels[job.status]} → ${EmploymentHistoryStatusLabels[newStatus]}`
      )
      await Promise.all([refetchTotalVerifiedMonthsEmployed(), refetchJobs()])
    } catch (error: any) {
      notify(`Unable to change status: ${error.response.data}`, { type: 'error' })
      captureExceptionIfProduction(error)
    }
  }

  useEffect(() => {
    const scrollHeight = notesWrap.current?.querySelector('.notes-list')?.scrollHeight
    if (scrollHeight && scrollHeight > NOTES_MAX_HEIGHT) {
      setShowSeeMore(true)
    }
  }, [notes?.length])

  return (
    <>
      <Relative>
        {!isFirst && <Divider mt={5} mb={5} color={gapBetweenJobs ? marigoldOrange : undefined} />}
        {gapBetweenJobs ? (
          <JobsGap>
            {formatMonths(gapBetweenJobs)} gap{' '}
            {previousJob && previousJob.details.unemployment_gap_before_reason ? (
              <>
                {' '}
                - {UnemploymentGapBeforeReasonDetail.get(previousJob.details.unemployment_gap_before_reason)}
              </>
            ) : null}
          </JobsGap>
        ) : null}
      </Relative>
      {!job.is_complete && (
        <ErrorMessage>
          <Text variant="header4">Job incomplete.</Text>
          <Text variant="paragraph3">
            <strong>Missing fields:</strong> {job.missing_fields_to_complete.join(', ')}
          </Text>
        </ErrorMessage>
      )}
      <JobWrap>
        <div>
          {isDriving ? (
            <Text variant="uppercaseLabel2" fontColor={cyanD1}>
              Class 8 job
            </Text>
          ) : (
            <Text variant="uppercaseLabel2" fontColor={midGray}>
              Non-class 8 job
            </Text>
          )}
          {job.details.position_held && (
            <Text variant="header2" fontColor={primaryBlack} mt={0.5}>
              {job.details.position_held}
            </Text>
          )}
          <Box mt={2} />
          <FlexBox>
            {job.details.dot_number && (
              <Text variant="header3" fontColor={primaryBlack}>
                Dot# {job.details.dot_number}
              </Text>
            )}
            {job.details.dot_number && job.details.is_own_authority && (
              <Text variant="header3" fontColor={primaryBlack} ml={1} mr={1}>
                -
              </Text>
            )}
            {job.details.is_own_authority && (
              <Text variant="header3" fontColor={primaryBlack}>
                {job.details.is_own_authority ? 'Owned' : 'Not owned'}
              </Text>
            )}
          </FlexBox>
          {job.details.company_name && (
            <Text variant="header3" fontColor={primaryBlack}>
              {job.details.company_name}
            </Text>
          )}
          {job.details.company_dba && (
            <Text variant="header3" fontColor={primaryBlack}>
              DBA: {job.details.company_dba}
            </Text>
          )}
          {job.details.company_address && (
            <Text variant="header3" fontColor={primaryBlack}>
              {job.details.company_address}
            </Text>
          )}
          {isDriving && (
            <>
              <Box mt={2} />
              {job.details.contact_name && (
                <Text variant="label3" fontColor={midGray}>
                  {job.details.contact_name}
                </Text>
              )}
              {job.details.contact_email && (
                <Text variant="label3" fontColor={midGray}>
                  {job.details.contact_email}
                </Text>
              )}
              {job.details.contact_phone && (
                <Text variant="label3" fontColor={midGray}>
                  {job.details.contact_phone}
                </Text>
              )}
            </>
          )}
          <Text
            variant="header4"
            fontColor={job.details.can_contact_employer ? successD1 : lightGray1}
            mt={1}
          >
            <FlexBox alignItems="center" gap="5px">
              <FlexBox styles="margin-top: -2px;">
                {job.details.can_contact_employer ? <CheckCircleIcon fontSize="small" /> : <CloseIcon />}
              </FlexBox>
              {job.details.can_contact_employer ? 'can' : 'can’t'} contact
            </FlexBox>
          </Text>
        </div>
        <div>
          <Text variant="header3" fontColor={lightGray1} mt={4}>
            Reported
          </Text>
          <Text variant="header3" fontColor={primaryBlack} mt={1}>
            {startDate} - {endDate} ({formatMonths(job.details.duration_in_months!)})
          </Text>

          {job.details.verified_dates?.length ? (
            <>
              <FlexBox alignItems="center" gap="5px" mt={1}>
                <Text variant="header3" fontColor={lightGray1}>
                  Verified
                </Text>
                <Text
                  variant="header3"
                  fontColor={lightGray1}
                  mt={0.2}
                  onClick={() => setIsEditingVerificationDates(job)}
                >
                  <StyledEditIcon />
                </Text>
              </FlexBox>
              {isEditingVerificationDates && (
                <UserEmploymentVerifyDates
                  userId={user.id}
                  job={job}
                  onSuccess={async () => {
                    await Promise.all([refetchTotalVerifiedMonthsEmployed(), refetchJobs()])
                    setIsEditingVerificationDates(null)
                  }}
                  onClose={() => setIsEditingVerificationDates(null)}
                  newStatus={null}
                />
              )}
              {job.details.verified_dates.map((d) => (
                <Text variant="header3" fontColor={primaryBlack} mt={1}>
                  {d.start_date_formatted} - {d.end_date_formatted} (
                  {formatMonths(
                    differenceInCalendarMonths(
                      new Date(
                        parseInt(d.end_date_formatted.split('/')[1]),
                        parseInt(d.end_date_formatted.split('/')[0]) - 1
                      ),
                      new Date(
                        parseInt(d.start_date_formatted.split('/')[1]),
                        parseInt(d.start_date_formatted.split('/')[0]) - 1
                      )
                    ) + 1
                  )}
                  )
                </Text>
              ))}
            </>
          ) : null}

          <Box mt={2.6} />
          <JobDetailsTable>
            <Text variant="header3" fontColor={lightGray1}>
              Subject to FMCSRs?
            </Text>
            <Text variant="label2" fontColor={darkGray}>
              {convertBooleanToYesNo(job.details.subject_to_fmcsr_regulations)}
            </Text>

            <Text variant="header3" fontColor={lightGray1}>
              Subject to D/A testing?
            </Text>
            <Text variant="label2" fontColor={darkGray}>
              {convertBooleanToYesNo(job.details.subject_to_drug_and_alcohol_testing)}
            </Text>

            <Text variant="header3" fontColor={lightGray1}>
              Reason for leaving
            </Text>
            <Text variant="label2" fontColor={darkGray}>
              {job.details.leaving_reason_label}
              {job.details.leaving_reason_label && ' - '}
              {job.details.leaving_reason_detail}
            </Text>

            {isDriving && (
              <>
                <Text variant="header3" fontColor={lightGray1}>
                  Equipment driven
                </Text>
                <Text variant="label2" fontColor={darkGray}>
                  {job.details.equipment_type_label}
                </Text>
              </>
            )}

            {isDriving && (
              <>
                <Text variant="header3" fontColor={lightGray1}>
                  Trailer type
                </Text>
                <Text variant="label2" fontColor={darkGray}>
                  {job.details.trailer_type_label}
                </Text>
              </>
            )}
          </JobDetailsTable>

          <Box mt={3} />
          <JobDetailsTable>
            <Text variant="uppercaseLabel2" fontColor={darkGray}>
              Documents ({[...job.console_documents, ...job.driver_documents].filter(Boolean).length})
            </Text>
            <Upload onFileUpload={handleFileUpload}>
              <Button variant="text" margin="0 auto 0 0" isLoading={isFileUploadLoading}>
                Upload new
              </Button>
            </Upload>

            {job.driver_documents.map((document) => (
              <React.Fragment key={document.url}>
                <Text variant="label3" fontColor={lightGray1}>
                  <TextClamp>{document.filename}</TextClamp>
                </Text>
                <ExternalLink variant="label2" href={document.url}>
                  View
                </ExternalLink>
              </React.Fragment>
            ))}
            {job.console_documents.map((document) => (
              <React.Fragment key={document.url}>
                <Text variant="label2" fontColor={lightGray1}>
                  {document.filename}
                </Text>
                <ExternalLink variant="label2" href={document.url}>
                  View
                </ExternalLink>
              </React.Fragment>
            ))}
          </JobDetailsTable>
        </div>
        <NotesWrap ref={notesWrap} $isExpanded={isExpanded}>
          {isDriving && (
            <>
              <FlexBox gap="20px">
                <div>
                  <EmploymentJobStatus job={job} onSuccess={handleJobStatusChange} userId={user.id} />
                </div>
                <div style={{ marginLeft: 'auto' }}>
                  <FlexBox gap="10px">
                    {canEdit && (
                      <Button
                        onClick={() => handleEdit(job)}
                        variant="secondary"
                        size="medium"
                        icon={<StyledEditIcon />}
                      />
                    )}
                  </FlexBox>
                </div>
              </FlexBox>
              <Box mt={2} />
              <Notes
                isStatic
                recordId={job.id}
                recordType="console/employment-history"
                recordName="driver"
                onNotesChange={setNotes}
                apiVersion="v1"
                renderHeaderButton={() =>
                  showSeeMore && (
                    <Button onClick={() => setIsExpanded(!isExpanded)} variant="text" size="medium">
                      See {isExpanded ? 'less' : 'more'}
                    </Button>
                  )
                }
              />
            </>
          )}
        </NotesWrap>
      </JobWrap>
    </>
  )
}

function EmploymentJobStatus({
  job,
  userId,
  onSuccess,
}: {
  job: EmploymentHistory
  userId: string
  onSuccess: HandlerOf<EmploymentHistoryStatus>
}) {
  const [isLoading, setIsLoading] = useState(false)
  const [newStatus, setNewStatus] = useState<EmploymentHistoryStatus | null>(null)
  const value = job.status

  function getColors() {
    if (value === EmploymentHistoryStatus.VERIFICATION_NOT_STARTED) return ['#E3E8EC', midGray]
    if (value === EmploymentHistoryStatus.IN_PROGRESS) return [warning, marigoldOrange]
    if (value === EmploymentHistoryStatus.VERIFIED_WITH_DISCREPANCY) return [success, kellyGreenD1]
    if (value === EmploymentHistoryStatus.VERIFIED) return [success, kellyGreenD1]
    return [error, errorD1]
  }

  const colors = getColors()

  const renderOpener = (isOpen: boolean) => {
    return (
      <DropdownWrap $color={colors[0]}>
        <FlexBox alignItems="center" gap="10px">
          <Text variant="paragraph2" fontColor={colors[1]} fontWeight={400}>
            {EmploymentHistoryStatusLabels[value]}
          </Text>
          <Arrow $isOpen={isOpen} $color={colors[1]} />
        </FlexBox>
      </DropdownWrap>
    )
  }

  async function handleSuccess() {
    setIsLoading(true)
    newStatus && (await onSuccess(newStatus))
    setNewStatus(null)
    setIsLoading(false)
  }

  return (
    <>
      <Dropdown
        onChange={setNewStatus}
        noMargin
        isFullWidth
        options={[
          {
            label: EmploymentHistoryStatusLabels[EmploymentHistoryStatus.VERIFICATION_NOT_STARTED],
            value: EmploymentHistoryStatus.VERIFICATION_NOT_STARTED,
          },
          {
            label: EmploymentHistoryStatusLabels[EmploymentHistoryStatus.IN_PROGRESS],
            value: EmploymentHistoryStatus.IN_PROGRESS,
          },
          {
            label: EmploymentHistoryStatusLabels[EmploymentHistoryStatus.VERIFIED_WITH_DISCREPANCY],
            value: EmploymentHistoryStatus.VERIFIED_WITH_DISCREPANCY,
          },
          {
            label: EmploymentHistoryStatusLabels[EmploymentHistoryStatus.VERIFIED],
            value: EmploymentHistoryStatus.VERIFIED,
          },
          {
            label: EmploymentHistoryStatusLabels[EmploymentHistoryStatus.UNABLE_TO_VERIFY],
            value: EmploymentHistoryStatus.UNABLE_TO_VERIFY,
          },
        ]}
        renderOpener={renderOpener}
        maxHeight={258}
        width={250}
      />
      {newStatus &&
        (newStatus === EmploymentHistoryStatus.VERIFIED_WITH_DISCREPANCY ? (
          <UserEmploymentVerifyDates
            userId={userId}
            job={job}
            onSuccess={handleSuccess}
            onClose={() => setNewStatus(null)}
            newStatus={newStatus}
          />
        ) : (
          <SimpleDialog onClose={() => setNewStatus(null)} isOpen maxWidth="472px">
            <Wrap width="700px" maxWidth="100%">
              <Text variant="header2" align="left">
                Change verification status to {EmploymentHistoryStatusLabels[newStatus]}?
              </Text>

              <Box mt={8} />
              <Button
                margin="0 auto"
                variant="primary"
                size="large"
                onClick={handleSuccess}
                isLoading={isLoading}
              >
                Change
              </Button>
              <Box m={3} />
              <Button margin="0 auto" variant="text" onClick={() => setNewStatus(null)}>
                Cancel
              </Button>
            </Wrap>
          </SimpleDialog>
        ))}
    </>
  )
}

function Upload({ children, onFileUpload }: { children: React.ReactNode; onFileUpload: HandlerOf<File> }) {
  const fileRef = useRef<HTMLInputElement | null>(null)

  function handleUpload() {
    fileRef.current?.click()
  }

  const handleFileClickUpload: ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    onFileUpload(file)
  }

  return (
    <>
      <span onClick={handleUpload}>{children}</span>
      <FileInput type="file" value="" ref={fileRef} onChange={handleFileClickUpload} />
    </>
  )
}

function convertBooleanToYesNo(val?: boolean) {
  if (val === true) return 'Yes'
  if (val === false) return 'No'
  return ''
}

const SummaryBox = styled.div`
  width: 550px;
  background: ${bgGray};
  border-radius: 7px;
  padding: 24px 30px;
`

const SummaryBoxLabel = styled.span`
  display: inline-block;
  width: 220px;
`

const JobsWrap = styled.div`
  width: 1045px;
`

const JobWrap = styled.div`
  display: grid;
  grid-template-columns: 190px 355px 1fr;
  gap: 40px;
`

const JobDetailsTable = styled.div`
  display: grid;
  grid-template-columns: 205px 1fr;
  gap: 20px;
  grid-row-gap: 10px;
`

const FileInput = styled.input`
  display: none;
`

const JobsGap = styled.div`
  color: ${marigoldOrange};
  border-radius: 4px;
  padding: 5px 10px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
`

const DropdownWrap = styled.div<{ $color: string }>`
  height: 28px;
  display: flex;
  justify-content: flex-start;
  cursor: pointer;
  padding: 0 10px;
  border-radius: 3px;

  ${(p) => p.$color && `background-color: ${p.$color};`}

  ${Arrow} {
    position: relative;
    top: -1px;
  }
`

const NotesWrap = styled.div<{ $isExpanded: boolean }>`
  max-height: 300px;
  display: flex;
  flex-direction: column;

  ${(p) => p.$isExpanded && `max-height: none;`}
`

const StyledEditIcon = styled(EditIcon)`
  font-size: 16px !important;
`

export const ErrorMessage = styled.div`
  padding: 10px 12px;
  border-radius: 6px;
  background: ${error};
  margin-bottom: 20px;
`
